import type { ReviewThreadListItemModel } from "@/api/cle-api";
import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import ArrowRightIcon from "@/components/icons/ArrowRightIcon";
import { StatusBadge } from "@/components/pages/person/id/Efteruddannelse/StatusBadge";
import TableBasic from "@/components/Table/TableBasic";
import {
  defaultReviewThreadColumns,
  type ReviewThreadColumnField,
} from "@/config/efteruddannelseConfig";
import useActiveColumns from "@/hooks/useActiveColumns";
import type { IColumn, SortBy } from "@/interfaces/table";
import { orderBy } from "@/util/orderBy";
import { getHeaderActiveColumns, TD } from "@/util/table";
import useLocalStorageState from "use-local-storage-state";
import { Link } from "react-router-dom";
import { DisplayNameAvatar } from "./DisplayNameAvatar";
import {
  formatReviewThreadStatus,
  getReviewThreadStatusVariant,
} from "./utils/reviewThreadStatus";

interface ReviewThreadsTableProps {
  threads: ReviewThreadListItemModel[];
  isLoading: boolean;
  error: Error | null;
  onViewThread: (thread: ReviewThreadListItemModel) => void;
}

const defaultSortBy: SortBy<ReviewThreadColumnField>[] = [
  { field: "createdAt", direction: "desc" },
];

const renderCell = (
  col: IColumn<ReviewThreadColumnField>,
  thread: ReviewThreadListItemModel,
) => {
  switch (col.field) {
    case "assignedAdminDisplayName":
      return (
        <td key={col.field}>
          <DisplayNameAvatar name={thread.assignedAdminDisplayName} />
        </td>
      );
    case "resolvedByAdminDisplayName":
      return (
        <td key={col.field}>
          <DisplayNameAvatar name={thread.resolvedByAdminDisplayName} />
        </td>
      );
    case "ownerDisplayName":
      return (
        <td key={col.field}>
          <Link
            to={`/person/${thread.ownerUserId}`}
            className="link-primary"
            onClick={(event) => event.stopPropagation()}
          >
            {thread.ownerDisplayName ?? "Navn på person"}
          </Link>
        </td>
      );
    case "status":
      return (
        <td key={col.field}>
          <StatusBadge
            status={formatReviewThreadStatus(thread.status)}
            variant={getReviewThreadStatusVariant(thread.status)}
          />
        </td>
      );
    default:
      return <TD key={col.field} col={col} rowData={thread} />;
  }
};

export const ReviewThreadsTable = ({
  threads,
  isLoading,
  error,
  onViewThread,
}: ReviewThreadsTableProps) => {
  const [columns, setActiveColumns] = useActiveColumns(
    "efteruddannelse-active-columns-v2",
    defaultReviewThreadColumns,
  );
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "efteruddannelse-sort-by",
    { defaultValue: defaultSortBy },
  );

  const activeColumnCount = columns.filter((col) => col.active).length;
  const colSpan = activeColumnCount + 1;

  const sortedThreads = orderBy(
    threads,
    sortBy.map((sort) => sort.field),
    sortBy.map((sort) => sort.direction),
  );

  const handleRowKeyDown = (
    event: React.KeyboardEvent<HTMLTableRowElement>,
    thread: ReviewThreadListItemModel,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onViewThread(thread);
    }
  };

  return (
    <TableBasic
      className="table--selectable"
      columns={columns}
      defaultColumns={defaultReviewThreadColumns}
      setActiveColumns={setActiveColumns}
      headerRow={
        <>
          {getHeaderActiveColumns(columns, {
            defaultSortBy,
            setSortBy,
            sortBy,
          })}
        </>
      }
    >
      {isLoading && (
        <tr>
          <td colSpan={colSpan} className="text-muted">
            Henter henvendelser ...
          </td>
        </tr>
      )}
      {error && (
        <tr>
          <td colSpan={colSpan}>
            <CleApiErrorText
              error={error}
              fallback="Kunne ikke hente henvendelser."
            />
          </td>
        </tr>
      )}
      {!isLoading &&
        !error &&
        sortedThreads.map((thread) => (
          <tr
            key={thread.id}
            tabIndex={0}
            onClick={() => onViewThread(thread)}
            onKeyDown={(event) => handleRowKeyDown(event, thread)}
          >
            {columns
              .filter((col) => col.active)
              .map((col) => renderCell(col, thread))}
            <td className="icon-cell">
              <span className="icon-cell-inner icon-cell-inner--pointer">
                <ArrowRightIcon width={18} />
              </span>
            </td>
          </tr>
        ))}
      {!isLoading && !error && !threads.length && (
        <tr>
          <td colSpan={colSpan} className="text-muted">
            Ingen henvendelser
          </td>
        </tr>
      )}
    </TableBasic>
  );
};
